import { useEffect, useState } from 'react';
import { apiFetch } from './api';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

async function requestJson(url, options) {
  const response = await apiFetch(url, options);
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || 'This verification request is unavailable.');
  return result;
}

export function PhoneApprovalPage() {
  const challenge = window.location.pathname.startsWith('/key/')
    ? decodeURIComponent(window.location.pathname.slice('/key/'.length))
    : new URLSearchParams(window.location.search).get('challenge') || '';
  const [state, setState] = useState({ loading: true, error: '', details: null, done: '' });
  useEffect(() => {
    if (!challenge) {
      setState({ loading: false, error: 'This verification request is invalid.', details: null, done: '' });
      return undefined;
    }
    requestJson(`/api/auth/phone-approval/details/${encodeURIComponent(challenge)}`)
      .then(async (details) => {
        if (details.purpose === 'setup' && details.status === 'PENDING') {
          try {
            const credential = await startRegistration({ optionsJSON: details.registrationOptions });
            const csrf = await requestJson('/api/auth/csrf');
            const verified = await requestJson('/api/auth/phone-approval/setup/verify', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf.csrfToken }, body: JSON.stringify({ challenge, credential }) });
            setState({ loading: false, error: '', details: { ...details, status: 'APPROVED' }, done: verified.twoFactorEnabled ? 'Two-factor authentication enabled.' : '' });
          } catch (error) {
            setState({ loading: false, error: error.message.includes('expired') ? error.message : "Unknown error\nWe couldn't complete phone verification.", details: null, done: '' });
          }
          return;
        }
        if (details.purpose === 'login' && details.status === 'PENDING' && details.authenticationOptions) {
          try {
            const credential = await startAuthentication({ optionsJSON: details.authenticationOptions });
            const csrf = await requestJson('/api/auth/csrf');
            const verified = await requestJson('/api/auth/phone-approval/login/verify', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf.csrfToken }, body: JSON.stringify({ challenge, credential }) });
            setState({ loading: false, error: '', details: { ...details, status: verified.status }, done: 'Sign-in verified.' });
          } catch (error) {
            setState({ loading: false, error: "Unknown error\nWe couldn't complete phone verification.", details: null, done: '' });
          }
          return;
        }
        setState({ loading: false, error: '', details, done: details.status === 'APPROVED' ? (details.purpose === 'setup' ? 'Two-factor authentication enabled.' : 'Sign-in verified.') : '' });
      })
      .catch((error) => setState({ loading: false, error: error.message, details: null, done: '' }));
    return undefined;
  }, [challenge]);

  useEffect(() => {
    if (!state.done) return undefined;
    const timer = window.setTimeout(() => { window.close(); }, 800);
    return () => window.clearTimeout(timer);
  }, [state.done]);

  const decide = async (decision) => {
    try {
      const csrf = await requestJson('/api/auth/csrf');
      const result = await requestJson('/api/auth/phone-approval/decision', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf.csrfToken }, body: JSON.stringify({ challenge, decision }) });
      setState((current) => ({ ...current, error: result.status === 'APPROVED' ? '' : result.message, done: result.status === 'APPROVED' ? 'Verification successful' : '', details: { ...current.details, status: result.status } }));
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    }
  };

  const setup = state.details?.purpose === 'setup';
  return <main className="auth-shell phone-approval-shell"><a className="brand" href="/" aria-label="Adorable Amigos home"><span>AA</span><div>Adorable Amigos<small>Metrology &amp; Quality</small></div></a><section className="auth-card phone-approval-card"><p className="kicker dark">Adorable Amigos · Metrology &amp; Quality</p><h1>{setup ? 'Confirm two-factor setup' : 'Approve sign-in'}</h1>{state.loading && <p>{setup ? 'Verifying…' : 'Checking this verification request…'}</p>}{state.error && <><p className="form-error" role="alert">{state.error}</p>{state.error.includes('Sign in') && <p><a href="/login">Sign in to approve this request</a></p>}</>}{state.details && !state.done && <><p>{setup ? 'This secure setup link is being verified.' : 'A sign-in to your Adorable Amigos account is waiting for approval.'}</p>{!setup && <dl className="phone-approval-details"><div><dt>Browser</dt><dd>{state.details.browser}</dd></div><div><dt>Device</dt><dd>{state.details.device}</dd></div><div><dt>Approximate region</dt><dd>{state.details.region}</dd></div><div><dt>Time</dt><dd>{new Date(state.details.createdAt).toLocaleString('en-GB')}</dd></div></dl>}{!setup && <><p>Is this you?</p><div className="phone-approval-actions"><button className="outline-button" type="button" onClick={() => decide('reject')}>Reject</button><button className="button dark-button" type="button" onClick={() => decide('approve')}>Approve sign-in</button></div></>}</>}{state.done && <p className="form-success" role="status">✓ {state.done}<br />{setup ? 'You can close this window.' : 'You can return to your other device.'}</p>}</section></main>;
}
